import { lazy, Suspense } from 'react';
import { AuthProvider, useAuth } from '@/utils/Auth';
import NavBar from './components/header/navBar/NavBar';
import { Routes, Route } from 'react-router';
import { Toaster } from 'sonner';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import { useTranslation } from 'react-i18next';

const MyDay = lazy(() => import('./components/myDay/MyDay'));
const Home = lazy(() => import('./components/home/Home'));
const Login = lazy(() => import('./components/login/Login'));
const ResetPassword = lazy(() => import('./components/login/ResetPassword'));
const MyDocs = lazy(() => import('./components/myDocs/MyDocs'));

import ProtectedRoute from './context/ProtectedRoute';
const CompanyManagement = lazy(
  () => import('./components/companyManagement/CompanyManagement')
);
const CompanyRegistration = lazy(
  () => import('./components/companyRegistration/CompanyRegistration')
);
const ApprovalsNew = lazy(() => import('./components/approvals/ApprovalsNew'));
import NotFound from './components/notFound/NotFound';
import { FirebaseProvider, useFirebase } from './context/FirebaseContext';
const MyDeparment = lazy(() => import('./components/myDeparment/MyDeparment'));

//const SetupData = lazy(() => import('./components/login/SetupData'));
import { LanguageProvider } from './context/LanguageContext';
import LoadingSpinner from './components/LoadingSpinner/LoadingSpinner';
import { Footer } from './components/home/Footer';
import AppUpdateNotification from './components/AppUpdateNotification/AppUpdateNotification';

import styles from './App.module.css';
//import CreationStepsWizard from './components/companyManagement/CreationStepsWizard';

//import { Toaster } from '@/components/ui/toaster';
//import TestComponent from './TestComponent';
console.log('Environment check:', {
  NODE_ENV: import.meta.env.NODE_ENV,
  MODE: import.meta.env.MODE,
  VITE_WORKER_DOWNLOAD_SINGLE: import.meta.env.VITE_WORKER_DOWNLOAD_SINGLE,
  allEnvVars: import.meta.env,
});

function LoadingFallback() {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-center h-screen bg-gray-50">
      <div className="text-center">
        <LoadingSpinner />
        <p className="mt-4 text-gray-600 text-lg">{t('Home.loading')}</p>
      </div>
    </div>
  );
}

// Create a wrapper component to handle auth state
function AppContent() {
  const { loading } = useAuth();

  // Show loading spinner while authentication is being determined
  if (loading) {
    return <LoadingFallback />;
  }

  return (
    <div className="flex flex-col min-h-screen">
      <LanguageProvider>
        <ErrorBoundary>
          <FirebaseProvider>
            <AppContentWithFirebase />
          </FirebaseProvider>
        </ErrorBoundary>
      </LanguageProvider>
    </div>
  );
}

// Separate component to handle Firebase loading state
function AppContentWithFirebase() {
  const { user } = useAuth();

  // Only check Firebase loading state if user is authenticated
  let isFirebaseLoading = false;
  if (user) {
    try {
      const { isLoading } = useFirebase();
      isFirebaseLoading = isLoading;
    } catch (error) {
      // Firebase context not available yet, consider it loading
      isFirebaseLoading = true;
    }
  }

  // Show loading spinner while Firebase data is loading for authenticated users
  if (user && isFirebaseLoading) {
    return <LoadingFallback />;
  }

  return (
    <>
      <NavBar />
      <div className="flex-grow pt-16 bg-gray-50">
        <Routes>
          {/* Define your routes here */}
          <Route
            path="/"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <Home />
              </Suspense>
            }
          />
          <Route
            path="/auth/callback/home"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <Home />
              </Suspense>
            }
          />
          <Route
            path="/home"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <Home />
              </Suspense>
            }
          />
          <Route
            path="/approvals"
            element={
              <ProtectedRoute requiredRoles={['Admin', 'Approver']}>
                <Suspense fallback={<LoadingFallback />}>
                  <ApprovalsNew />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/mydepartment"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <div className={styles.container}>
                    <MyDeparment />
                  </div>
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/company-management"
            element={
              <ProtectedRoute requiredRoles={['Admin']}>
                <Suspense fallback={<LoadingFallback />}>
                  <CompanyManagement />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/company-registration"
            element={
              <ProtectedRoute requiredRoles={['SuperAdmin']}>
                <Suspense fallback={<LoadingFallback />}>
                  <CompanyRegistration />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/myday"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <div className={styles.container}>
                    <MyDay />
                  </div>
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <ResetPassword />
              </Suspense>
            }
          />
          <Route
            path="/mydocs"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <div className={styles.container}>
                    <MyDocs />
                  </div>
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/login"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <ErrorBoundary>
                  <Login />
                </ErrorBoundary>
              </Suspense>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
      <Toaster
        richColors
        closeButton={true}
        theme="light"
        className="toaster group z-500"
        toastOptions={{
          classNames: {
            title: 'text-lg font-semibold', // Makes title larger
            description: 'text-base', // Makes description text larger
            closeButton: 'absolute right-1 top-4 left-auto',
          },
        }}
      />
      <AppUpdateNotification />
      <Footer />
    </>
  );
}

function App() {
  console.log('checkENV', import.meta.env.DEV);

  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
